import { NOT_SURE, ERROR } from './constants';

import { resetSystemSlice, addSystemNotice } from '../slices/system/system.slice';
import { resetPartySlice } from '../slices/party/party.slice';
import { resetChatSlice } from '../slices/chat/chat.slice';
import { resetProductSlice } from '../slices/product/product.slice';
import { resetContractSlice } from '../slices/contract/contract.slice';
import { resetProductManagementSlice } from '../slices/admin/product-management/product-management.slice';
import { resetDealRoomManagementSlice } from '../slices/admin/deal-room-management/deal-room-management.slice';
import { resetAccountManagementSlice } from '../slices/admin/account-management/account-management.slice';
import { resetMarketBuzzSlice } from '../slices/market-buzz/market-buzz.slice';
import { resetDashboardSlice } from '../slices/dashboard/dashboard.slice';
import { resetUserSlice } from '../slices/user/user.slice';
import { resetEWalletSlice } from '../slices/e-wallet/e-wallet.slice';
import { resetHelpCenterSlice } from '../slices/help-center/help-center.slice';
import { resetUserRoleManagementSlice } from '../slices/admin/user-role-management/user-role-management.slice';
import { resetReportSlice } from '../slices/report/report.slice';
import { resetCurrencySlice } from '../slices/currency/currency.slice';

export const noOp = () => {};

export const getClassNames = (baseClassName, modifiers) => {
  const classes = [baseClassName];

  Object.entries(modifiers).forEach(([name, active]) => {
    if (!active) return;

    classes.push(`${baseClassName}--${name}`);
  });

  return classes.join(' ');
};

export const isNumeric = (string) => {
  if (typeof string != 'string') return false;
  return !isNaN(string) && !isNaN(parseFloat(string));
};

export const saveToLocalStorage = (key, value) => localStorage.setItem(key, JSON.stringify(value));

export const retrieveFromLocalStorage = (key) => JSON.parse(localStorage.getItem(key));

export const clearFromLocalStorage = (key) => localStorage.removeItem(key);

export const generateUUID = () => crypto.randomUUID();

export const resetState = (dispatch) => {
  dispatch(resetSystemSlice());
  dispatch(resetPartySlice());
  dispatch(resetChatSlice());
  dispatch(resetProductSlice());
  dispatch(resetContractSlice());
  dispatch(resetProductManagementSlice());
  dispatch(resetDealRoomManagementSlice());
  dispatch(resetAccountManagementSlice());
  dispatch(resetMarketBuzzSlice());
  dispatch(resetDashboardSlice());
  dispatch(resetUserSlice());
  dispatch(resetEWalletSlice());
  dispatch(resetHelpCenterSlice());
  dispatch(resetUserRoleManagementSlice());
  dispatch(resetReportSlice());
  dispatch(resetCurrencySlice());
  localStorage.clear();
};

export const toBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = (error) => reject(error);
  });
};

export const getFileExtension = (fileName) => {
  const split = fileName.split('.');
  return split[split.length - 1];
};

export const isToday = (date) => {
  const today = new Date();
  const dateToCheck = new Date(date);

  return (
    dateToCheck.getDate() === today.getDate() &&
    dateToCheck.getMonth() === today.getMonth() &&
    dateToCheck.getFullYear() === today.getFullYear()
  );
};

export const adjustDateTimeZone = (date) => {
  const dateToAdjust = new Date(date);
  const offset = dateToAdjust.getTimezoneOffset() * 60000;
  return new Date(dateToAdjust.getTime() + offset);
};

export const formatDate = (dateToFormat, includeTime, excludeYear, shortMonth) => {
  if (!dateToFormat) {
    return '';
  }

  const date = adjustDateTimeZone(dateToFormat);

  if (isNaN(date.getTime())) {
    return '';
  }

  const day = date.getDate();
  const month = date.toLocaleString('default', { month: shortMonth ? 'short' : 'long' });
  const year = date.getFullYear();

  if (!includeTime && !excludeYear) {
    return `${day} ${month} ${year}`;
  } else if (!includeTime && excludeYear) {
    return `${day} ${month}`;
  }

  let hours = date.getHours();
  let minutes = date.getMinutes();

  if (hours < 10) {
    hours = `0${hours}`;
  }

  if (minutes < 10) {
    minutes = `0${minutes}`;
  }

  if (!excludeYear) {
    return `${day} ${month} ${year} ${hours}:${minutes}`;
  } else {
    return `${day} ${month} ${hours}:${minutes}`;
  }
};

export const extractUnitAndMetric = (string) => {
  if (!string) return ['', ''];

  const splitString = string.split(' ');
  const unit = splitString[0];
  const metric = splitString.slice(1).join(' ');

  return [unit, metric];
};

export const extractCountryAndCity = (string) => {
  if (!string) return ['', ''];

  const splitString = string.split(', ');
  const country = splitString[1];
  const city = splitString[0];

  return [country, city];
};

export const filterListings = (listings, searchString) => {
  if (!searchString) return listings;

  return listings.filter((listing) => {
    const category = listing.category.name;
    const classification = listing.classification.name;
    const type = listing.specification.name;
    const name = listing.brandName;

    const stringToFilter = `${classification} ${category} ${type} ${name}`;
    if (stringToFilter.toLowerCase().includes(searchString.toLowerCase())) {
      return listing;
    }

    return null;
  });
};

export const toCamelCase = (string) => {
  let copy = string.repeat(1);
  copy = copy
    .replace(/\s(.)/g, ($1) => $1.toUpperCase())
    .replace(/\s/g, '')
    .replace(/^(.)/, ($1) => $1.toLowerCase());
  return copy;
};

export const removeCamelCase = (string) => {
  let copy = string.repeat(1);
  copy = copy
    .replace(/([A-Z]+)/g, ' $1')
    .replace(/([A-Z][a-z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase());
  return copy;
};

export const formatBytes = (bytes, decimals = 0) => {
  if (bytes === 0) return '0 B';

  const kilobyte = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];

  const i = Math.floor(Math.log(bytes) / Math.log(kilobyte));

  return `${parseFloat((bytes / Math.pow(kilobyte, i)).toFixed(decimals))} ${sizes[i]}`;
};

export const formatMetric = (metric, unit = 2) => {
  if (!metric) return '';

  metric = metric.slice(0, -3);

  if (unit > 1) {
    return metric.concat('s');
  }

  return metric;
};

export const formatNumber = (number) => {
  if (number === undefined || number === null) return '';

  return parseFloat(number)
    .toFixed(2)
    .replace(/[.,]00$/, '');
};

export const isImage = (fileName) => {
  const fileExtension = fileName.split('.').pop();
  return ['jpg', 'jpeg', 'png', 'webp'].includes(fileExtension);
};

export const findComment = (comments, commentExternalReference) => {
  if (!comments) return null;

  const comment = comments.find((comment) => comment.externalReference === commentExternalReference);

  if (!comment) {
    for (const comment of comments) {
      const foundComment = findComment(comment.comments, commentExternalReference);

      if (foundComment) {
        return foundComment;
      }
    }
  }

  return comment;
};

export const sortArray = (arrayToSort, excludeNotSure) => {
  let sortedArray = [...arrayToSort];
  sortedArray.sort((typeOne, typeTwo) => {
    const lowerTypeOne = typeOne.name.toLowerCase();
    const lowerTypeTwo = typeTwo.name.toLowerCase();

    if (lowerTypeOne < lowerTypeTwo) return -1;
    if (lowerTypeOne > lowerTypeTwo) return 1;
    return 0;
  });

  if (!excludeNotSure) {
    const newElement = { name: NOT_SURE, externalReference: NOT_SURE };
    sortedArray.push(newElement);
  }

  return sortedArray;
};

export const showErrorToast = (dispatch, message) => {
  dispatch(addSystemNotice({ type: ERROR, message }));
};

export const isURL = (string) => {
  return string.startsWith('https://') || string.startsWith('http://');
};

export const getFirstDayOfCurrentMonth = () => {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), 1, 12);
};

export const getLastDayOfCurrentMonth = () => {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 12);
};

export const openInNewTab = (url) => {
  if (!url) return;

  let copyOfUrl = url.repeat(1);

  if (!url.startsWith('http')) {
    copyOfUrl = `http://${url}`;
  }

  window.open(copyOfUrl, '_blank', 'noopener, noreferrer');
};

export const getPreference = (name) => {
  const preferences = retrieveFromLocalStorage('preferences');
  if (!preferences) return null;

  const preference = preferences.find((preference) => preference.name === name);
  if (!preference) return null;

  return preference.value;
};
